/**
 * Resolve a stored image value to a URL against the CURRENT file host.
 *
 * Uploaded images are persisted as a host-relative `/uploads/<file>` path
 * (see `uploadImage`). Older rows may still hold a host-baked absolute URL
 * such as `http://localplatform.com:3060/uploads/<file>`. Both are re-anchored
 * here to the current API host, so the app never requests an image from a
 * stale host (e.g. localhost in production).
 *
 * Any value WITHOUT a `/uploads/` segment — external URLs, `data:` / `blob:`
 * URIs, static assets like `/xk-icon.svg` — passes through untouched, so this
 * is safe to apply to every `<img>` source indiscriminately.
 */

function originOf(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return new URL(raw).origin;
  } catch {
    return '';
  }
}

/**
 * Origin (scheme + host) that serves uploaded files. Uploads are served by the
 * same backend that serves the API (static `/uploads`), so this is always the
 * API origin — deriving it from `VITE_API_URL` keeps the file host in lockstep
 * with the API host and avoids drift from a separate, easily-stale var.
 */
const FILE_ORIGIN = originOf(import.meta.env.VITE_API_URL);

export function resolveImageUrl(value: string | null | undefined): string {
  if (!value) return '';
  const v = value.trim();
  if (!v) return '';

  // Relative `/uploads/<file>` or host-baked `http(s)://host/uploads/<file>`
  // — keep everything from `/uploads/` onward, re-anchored to the live host.
  const marker = v.indexOf('/uploads/');
  if (marker !== -1) {
    return `${FILE_ORIGIN}${v.slice(marker)}`;
  }

  // No `/uploads/` segment: external URL, data: / blob: URI, or a static
  // asset — leave untouched.
  return v;
}
